<template>
  <div class="sidebar-shell fixed top-0 left-0 h-screen bg-[#162538] flex flex-col z-20"
       :class="isCollapsed ? 'is-collapsed w-[72px]' : 'is-expanded w-[260px]'">
    <!-- Header with logo and language switcher -->
    <div class="sidebar-header h-[80px] flex items-center relative border-white/10 shrink-0 w-full px-[20px]"
         :class="[isCollapsed ? 'justify-center px-0 border-b-0' : 'justify-start border-b-[0.5px]']">
      <div class="sidebar-brand flex flex-col font-['SF_Pro_Display',sans-serif] font-medium justify-center leading-[0] text-white whitespace-nowrap overflow-hidden transition-premium" 
           :class="[isCollapsed ? 'text-[24px]' : 'text-[28px]']"
           style="letter-spacing: 0.5px;">
        <transition name="brand-switch" mode="out-in">
          <p v-if="!isCollapsed" key="brand-full" class="leading-[24px] m-0">EMDEU</p>
          <div v-else key="brand-icon" class="logo-icon-wrapper">
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5.33331 16H13C14.6568 16 16 17.3431 16 19V26.6667" stroke="white" stroke-width="1.5"/>
              <path d="M26.6667 16L19 16C17.3432 16 16 14.6569 16 13L16 5.33333" stroke="white" stroke-width="1.5"/>
            </svg>
          </div>
        </transition>
      </div>
      <div class="sidebar-language absolute right-[20px] transition-premium" :class="isCollapsed ? 'opacity-0 pointer-events-none translate-x-3' : 'opacity-100 translate-x-0'">
        <LanguageSwitcher class="!bg-[#fcfeff] !text-[#778696] !border-none !rounded-[24px] !px-[12px] !py-[6px] !font-['Roboto:Regular',sans-serif] !text-[14px] !leading-[20px]" />
      </div>
    </div>
    
    <!-- Toggle button -->
    <button
      type="button"
      class="toggle-btn"
      :aria-label="isCollapsed ? 'Открыть боковую панель' : 'Свернуть боковую панель'"
      @click="toggleSidebar"
      :class="isCollapsed ? 'collapsed' : ''"
    >
      <svg 
        class="toggle-icon" 
        :class="{ 'rotated': isCollapsed }"
        width="16" height="16" viewBox="0 0 16 16" fill="none" 
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M10 12L6 8L10 4" stroke="white" stroke-width="0.9" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </button>

    <!-- Navigation menu -->
    <nav class="sidebar-nav flex-1 py-6 flex flex-col gap-[8px] overflow-y-auto overflow-x-hidden shrink-0 w-full transition-premium px-[16px]">
      <template v-for="item in menuItems" :key="item.name">
      <template v-if="item.section">
        <div
          v-if="isCollapsed"
          aria-hidden="true"
          class="mx-[14px] my-[6px] h-px shrink-0 bg-white/10"
          :class="{ hidden: item.name === menuItems[0].name }"
        ></div>
        <div
          v-else
          class="shrink-0 px-[10px] pb-[2px] text-[11px] font-['Roboto',sans-serif] font-medium uppercase leading-[16px] tracking-[0.08em] text-white/55"
          :class="item.name === menuItems[0].name ? 'pt-0' : 'pt-[12px]'"
        >
          {{ item.label }}
        </div>
      </template>
      <router-link 
        v-else
        :to="item.path"
        class="sidebar-item group flex gap-[16px] items-center p-[10px] rounded-[10px] text-[15px] font-['Roboto',sans-serif] font-light transition-premium relative shrink-0 w-full nav-link"
        :class="[
          isActive(item.path) ? 'active bg-[#0066FF] text-white' : 'text-white/80 hover:text-white hover:bg-white/10'
        ]"
        :title="isCollapsed ? item.label : ''"
      >
        <div v-if="item.iconType === 'svg'" class="svg-icon-wrapper transition-premium" :class="isActive(item.path) ? 'text-white' : 'text-white opacity-90'">
          <!-- Navigation SVGs -->
          <svg v-if="item.iconName === 'SvgIconChart'" width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M6.66667 10.8333V14.1667M13.3333 9.16667V14.1667M10 5.83333V14.1667M6.5 17.5H13.5C14.9 17.5 15.6 17.5 16.135 17.2275C16.6054 16.9878 16.9878 16.6054 17.2275 16.135C17.5 15.6 17.5 14.9 17.5 13.5V6.5C17.5 5.1 17.5 4.4 17.2275 3.865C16.9878 3.39462 16.6054 3.01218 16.135 2.7725C15.6 2.5 14.9 2.5 13.5 2.5H6.5C5.1 2.5 4.4 2.5 3.865 2.7725C3.39462 3.01218 3.01218 3.39462 2.7725 3.865C2.5 4.4 2.5 5.1 2.5 6.5V13.5C2.5 14.9 2.5 15.6 2.7725 16.135C3.01218 16.6054 3.39462 16.9687 3.865 17.2275C4.4 17.5 5.1 17.5 6.5 17.5Z" stroke="currentColor" stroke-width="0.9" stroke-linecap="round" stroke-linejoin="round"/></svg>
          <svg v-else-if="item.iconName === 'SvgIconList'" width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M11.6666 9.16699H6.66665M8.33331 12.5003H6.66665M13.3333 5.83366H6.66665M16.6666 5.66699V14.3337C16.6666 15.7338 16.6666 16.4338 16.3941 16.9687C16.1545 17.4391 15.7721 17.8215 15.3016 18.0612C14.7668 18.3337 14.0668 18.3337 12.6666 18.3337H7.33331C5.93318 18.3337 5.23311 18.3337 4.69834 18.0612C4.22793 17.8215 3.84554 17.4391 3.6058 16.9687C3.33331 16.4338 3.33331 15.7338 3.33331 14.3337V5.66699C3.33331 4.26686 3.33331 3.56679 3.6058 3.03202C3.84554 2.56161 4.22793 2.17916 4.69834 1.93948C5.23311 1.66699 5.93318 1.66699 7.33331 1.66699H12.6666C14.0668 1.66699 14.7668 1.66699 15.3016 1.93948C15.7721 2.17916 16.1545 2.56161 16.3941 3.03202C16.6666 3.56679 16.6666 4.26686 16.6666 5.66699Z" stroke="currentColor" stroke-width="0.9" stroke-linecap="round" stroke-linejoin="round"/></svg>
          <svg v-else-if="item.iconName === 'SvgIconUsers'" width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M15 13.1974C16.2133 13.8069 17.2534 14.785 18.0127 16.008C18.163 16.2503 18.2382 16.3713 18.2642 16.539C18.317 16.8798 18.084 17.2988 17.7666 17.4337C17.6104 17.5 17.4347 17.5 17.0834 17.5M13.3334 9.61017C14.5681 8.99658 15.4167 7.72238 15.4167 6.25C15.4167 4.77762 14.5681 3.50343 13.3334 2.8898M11.6667 6.25C11.6667 8.32107 9.98769 10 7.91666 10C5.84559 10 4.16666 8.32107 4.16666 6.25C4.16666 4.17893 5.84559 2.5 7.91666 2.5C9.98769 2.5 11.6667 4.17893 11.6667 6.25ZM2.13268 15.7819C3.46127 13.7872 5.5578 12.5 7.91666 12.5C10.2755 12.5 12.372 13.7872 13.7006 15.7819C13.9917 16.219 14.1372 16.4375 14.1204 16.7166C14.1074 16.9339 13.9649 17.2 13.7913 17.3313C13.5683 17.5 13.2615 17.5 12.648 17.5H3.18529C2.5718 17.5 2.26506 17.5 2.04202 17.3313C1.86836 17.2 1.7259 16.9339 1.71285 16.7166C1.6961 16.4375 1.84162 16.219 2.13268 15.7819Z" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>
          <svg v-else-if="item.iconName === 'SvgIconFlame'" width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M10 18.3337C13.7143 18.3337 16.6667 16.4818 16.6667 11.8522C16.6667 7.22255 11.9048 7.22255 11.9048 1.66699C9.04763 3.51884 7.74287 5.46329 7.14287 9.0744C6.17471 8.41101 5.49559 7.42063 5.23811 6.29662C4.28572 7.22255 3.33334 10.0003 3.33334 11.8522C3.33334 14.7596 4.55239 18.3337 10 18.3337Z" stroke="currentColor" stroke-width="0.9" stroke-linecap="round" stroke-linejoin="round"/></svg>
          <svg v-else-if="item.iconName === 'SvgIconPulse'" width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M10.0386 12.0196C11.4989 13.0806 13.4764 13.0806 14.9368 12.0196C15.2021 11.8268 15.4758 11.5531 16.0232 11.0057C16.5706 10.4583 16.8443 10.1846 17.0371 9.91928C18.0618 8.50886 18.0969 6.61608 17.1422 5.17266L14.8447 7.47018C14.1938 8.12105 13.1385 8.12105 12.4877 7.47018C11.8368 6.8193 11.8368 5.76403 12.4877 5.11316L14.7852 2.81564C13.3418 1.86099 11.449 1.89603 10.0386 2.92076C9.77326 3.11351 9.49955 3.38722 8.95213 3.93464C8.40471 4.48207 8.131 4.75578 7.93825 5.02107C6.87723 6.48144 6.87723 8.45892 7.93825 9.91928M10.0386 12.0196C9.77326 11.8268 9.49955 11.5531 8.95213 11.0057C8.40471 10.4583 8.131 10.1846 7.93825 9.91928M10.0386 12.0196C10.1817 12.1236 10.3299 12.2167 10.482 12.3003L6.78736 16.8762C5.81006 18.0866 3.99913 18.1828 2.89909 17.0827C1.79663 15.9803 1.89606 14.1646 3.11231 13.1891L7.68346 9.52285C7.76022 9.65826 7.84515 9.79114 7.93825 9.91928" stroke="currentColor" stroke-width="0.9" stroke-linecap="round" stroke-linejoin="round"/></svg>
          <svg v-else-if="item.iconName === 'SvgIconBook'" width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M16.6667 15.8337V13.3337H5.83334C4.45264 13.3337 3.33334 14.4529 3.33334 15.8337M7.33334 18.3337H14C14.9334 18.3337 15.4002 18.3337 15.7567 18.152C16.0703 17.9922 16.3253 17.7372 16.485 17.4237C16.6667 17.0672 16.6667 16.6004 16.6667 15.667V4.33366C16.6667 3.40023 16.6667 2.93353 16.485 2.57701C16.3252 2.2634 16.0703 2.00843 15.7567 1.84865C15.4002 1.66699 14.9334 1.66699 14 1.66699H7.33334C5.93321 1.66699 5.23314 1.66699 4.69837 1.93948C4.22796 2.17916 3.84551 2.56161 3.60583 3.03202C3.33334 3.56679 3.33334 4.26686 3.33334 5.66699V14.3337C3.33334 15.7338 3.33334 16.4338 3.60583 16.9687C3.84551 17.4391 4.22796 17.8215 4.69837 18.0612C5.23314 18.3337 5.93321 18.3337 7.33334 18.3337Z" stroke="currentColor" stroke-width="0.9" stroke-linecap="round" stroke-linejoin="round"/></svg>
          <svg v-else-if="item.iconName === 'SvgIconUltrasound'" width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M4.167 3.333h7.5a2.5 2.5 0 0 1 2.5 2.5v5a2.5 2.5 0 0 1-2.5 2.5h-7.5v-10Z" stroke="currentColor" stroke-width="1.1" stroke-linecap="round" stroke-linejoin="round"/><path d="M14.167 6.667h1.666A1.667 1.667 0 0 1 17.5 8.333v5.834a2.5 2.5 0 0 1-2.5 2.5H9.167M6.667 6.667h5M6.667 10h3.75M6.667 13.333v3.334M4.167 16.667h5" stroke="currentColor" stroke-width="1.1" stroke-linecap="round" stroke-linejoin="round"/></svg>
          <svg v-else-if="item.iconName === 'SvgIconAccount'" width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M10 9.16667C11.8409 9.16667 13.3333 7.67428 13.3333 5.83333C13.3333 3.99238 11.8409 2.5 10 2.5C8.15905 2.5 6.66667 3.99238 6.66667 5.83333C6.66667 7.67428 8.15905 9.16667 10 9.16667ZM10 9.16667C5.85786 9.16667 2.5 12.5245 2.5 16.6667C2.5 16.9483 2.51553 17.2264 2.54577 17.5M10 9.16667C14.1421 9.16667 17.5 12.5245 17.5 16.6667C17.5 16.9483 17.4845 17.2264 17.4542 17.5" stroke="currentColor" stroke-width="0.9" stroke-linecap="round" stroke-linejoin="round"/></svg>
          <svg v-else-if="item.iconName === 'SvgIconCalendar'" width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.1" stroke-linecap="round" stroke-linejoin="round"><rect x="2.5" y="3.5" width="15" height="14" rx="3"/><path d="M2.5 8h15M6.5 2v3M13.5 2v3M6 11.5h2M9 11.5h2M12 11.5h2M6 14.5h2"/></svg>
          <svg v-else-if="item.iconName === 'SvgIconClock'" width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.1" stroke-linecap="round" stroke-linejoin="round"><circle cx="10" cy="10" r="7.5"/><path d="M10 5.8V10l2.9 1.7"/></svg>
          <svg v-else-if="item.iconName === 'SvgIconWallet'" width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.1" stroke-linecap="round" stroke-linejoin="round"><rect x="2.5" y="5" width="15" height="11" rx="2.5"/><path d="M2.5 8.5h15M13 12.5h1.5"/></svg>
          <svg v-else-if="item.iconName === 'SvgIconSliders'" width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.1" stroke-linecap="round" stroke-linejoin="round"><path d="M3.3 5.8h7.5M15 5.8h1.7M3.3 10h2.5M9.2 10h7.5M3.3 14.2h9.2M15.8 14.2h.9"/><circle cx="12.9" cy="5.8" r="1.7"/><circle cx="7.5" cy="10" r="1.7"/><circle cx="14.2" cy="14.2" r="1.7"/></svg>
          <svg v-else-if="item.iconName === 'SvgIconShield'" width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M7.91699 9.61347L9.68476 11.3812L12.631 8.43496M4.16698 13.5719C3.13839 11.904 2.74007 9.33374 2.58816 7.41335C2.52488 6.6133 2.49323 6.21326 2.63145 5.7598C2.7431 5.39351 2.99965 4.97102 3.27317 4.70302C3.61177 4.37125 4.03159 4.19355 4.87121 3.83815L8.44112 2.32708C9.01618 2.08366 9.30371 1.96196 9.60161 1.91383C10.6969 1.96196 10.9845 2.08366 11.5595 2.32708L15.1294 3.83815C15.969 4.19355 16.3889 4.37125 16.7275 4.70302C17.001 4.97102 17.2575 5.39351 17.3692 5.7598C17.5074 6.21326 17.4758 6.21326 17.4125 7.41335C17.2606 9.33374 16.8622 11.904 15.8336 13.5719C14.8799 15.1184 13.0766 16.4696 11.7263 17.3337C11.2319 17.6501 10.9847 17.8083 10.5647 17.9057C10.2631 17.9756 9.73753 17.9756 9.43594 17.9057C9.01591 17.8083 8.7687 17.6501 8.27428 17.3337C6.92403 16.4696 5.12073 15.1184 4.16698 13.5719Z" stroke="currentColor" stroke-width="0.9" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </div>
        <img v-else-if="item.icon" :src="item.icon" class="w-[20px] h-[20px] flex-shrink-0 transition-premium" :class="isActive(item.path) ? 'brightness-200 contrast-200' : ''" />
        <span class="leading-[20px] w-full text-left overflow-hidden transition-premium nav-text" 
              :class="isCollapsed ? 'collapsed' : 'expanded'">{{ item.label }}</span>
        <span
          v-if="item.badgeKey && managerQueues[item.badgeKey] > 0 && !isCollapsed"
          class="ml-auto min-w-[22px] rounded-full px-[7px] py-[1px] text-center text-[12px] font-medium"
          :class="isActive(item.path) ? 'bg-white text-[#0057D9]' : 'bg-[#0066FF] text-white'"
        >
          {{ managerQueues[item.badgeKey] }}
        </span>
      </router-link>
      </template>
    </nav>
    
    <!-- Documents section -->
    <div class="w-full flex flex-col gap-[8px] items-start relative shrink-0 px-[16px]">
      <div class="w-full flex flex-col text-white pt-[12px] border-white/10 transition-premium border-t-[0.5px]">
        <router-link 
          to="/privacy-policy"
          class="sidebar-item flex items-center gap-[16px] p-[10px] rounded-[10px] hover:bg-white/10 transition-premium w-full group nav-link"
          :title="isCollapsed ? 'Политика конфиденциальности' : ''"
        >
          <div class="svg-icon-wrapper text-white/90 group-hover:text-white transition-premium">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M7.91699 9.61347L9.68476 11.3812L12.631 8.43496M4.16698 13.5719C3.13839 11.904 2.74007 9.33374 2.58816 7.41335C2.52488 6.6133 2.49323 6.21326 2.63145 5.7598C2.7431 5.39351 2.99965 4.97102 3.27317 4.70302C3.61177 4.37125 4.03159 4.19355 4.87121 3.83815L8.44112 2.32708C9.01618 2.08366 9.30371 1.96196 9.60161 1.91383C10.399 1.91383 10.1349 1.87117 9.8657 1.91383C10.6969 1.96196 10.9845 2.08366 11.5595 2.32708L15.1294 3.83815C15.969 4.19355 16.3889 4.37125 16.7275 4.70302C17.001 4.97102 17.2575 5.39351 17.3692 5.7598C17.5074 6.21326 17.4758 6.21329 17.4125 7.41335C17.2606 9.33374 16.8622 11.904 15.8336 13.5719C14.8799 15.1184 13.0766 16.4696 11.7263 17.3337C11.2319 17.6501 10.9847 17.8083 10.5647 17.9057C10.2631 17.9756 9.73753 17.9756 9.43594 17.9057C9.01591 17.8083 8.7687 17.6501 8.27428 17.3337C6.92403 16.4696 5.12073 15.1184 4.16698 13.5719Z" stroke="currentColor" stroke-width="0.9" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </div>
          <span class="text-[14px] font-light leading-[18px] text-left overflow-hidden transition-premium nav-text" :class="isCollapsed ? 'collapsed' : 'expanded'">Политика конфиденциальности</span>
        </router-link>
        
        <router-link 
          to="/user-agreement"
          class="sidebar-item flex items-center gap-[16px] p-[10px] rounded-[10px] hover:bg-white/10 transition-premium mt-[4px] w-full group nav-link"
          :title="isCollapsed ? 'Пользовательское соглашение' : ''"
        >
          <div class="svg-icon-wrapper text-white/90 group-hover:text-white transition-premium">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M11.6667 1.96257V4.00016C11.6667 4.93358 11.6667 5.40029 11.8483 5.75681C12.0081 6.07042 12.2631 6.32538 12.5767 6.48517C12.9332 6.66683 13.3999 6.66683 14.3333 6.66683H17.0331M11.6667 1.96257C11.4522 1.87716 11.2307 1.80962 11.0044 1.76079C10.5699 1.66699 10.1104 1.66699 9.19137 1.66699C6.84135 1.66699 5.66634 1.66699 4.77504 2.12113C3.99103 2.5206 3.35361 3.15802 2.95414 3.94203C2.5 4.83333 2.5 6.00011 2.5 8.33366V11.667C2.5 14.0005 2.5 15.1673 2.95414 16.0586C3.35361 16.8426 3.99103 17.48 4.77504 17.8795C5.66634 18.3337 6.83311 18.3337 9.16667 18.3337H10.8333C13.1669 18.3337 14.3337 18.3337 15.225 17.8795C16.009 17.48 16.6464 16.009 17.0459 15.225C17.5 14.3337 17.5 13.1669 17.5 10.8333V9.16667C17.5 9.06985 17.5 8.97504 17.5 8.88217M11.6667 1.96257C11.8176 2.02268 11.9652 2.09165 12.1084 2.16918C12.4994 2.38077 12.846 2.67783 13.5391 3.27195L15.1719 4.67151C16.0282 5.40548 16.4564 5.77247 16.7639 6.21862C16.8631 6.36249 16.953 6.51221 17.0331 6.66683M6.66667 10.8337H13.3333M6.66667 14.167H10.8333" stroke="currentColor" stroke-width="0.9" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </div>
          <span class="text-[14px] font-light leading-[18px] text-left overflow-hidden transition-premium nav-text" :class="isCollapsed ? 'collapsed' : 'expanded'">Пользовательское соглашение</span>
        </router-link>
      </div>
    </div>
    
    <!-- User info section -->
    <div class="flex flex-col gap-[8px] w-full pb-[20px] transition-premium shrink-0 px-[16px]">
      <div v-if="userInfo" class="flex items-center bg-[#162538] w-full">
        <div class="sidebar-profile-card flex gap-[12px] items-center py-[8px] rounded-[16px] transition-premium hover:bg-white/10 cursor-pointer w-full group overflow-hidden px-0">
          <div class="relative grid inline-grid leading-[0] place-items-start shrink-0">
            <!-- Profile photo if available -->
            <img v-if="userInfo.user && userInfo.user.face_photo" 
                 :src="userInfo.user.face_photo" 
                 :alt="formatDoctorName()" 
                 class="col-1 ml-0 mt-0 relative rounded-[20px] row-1 size-[40px] object-cover ring-2 ring-[#162538]"
                 @error="onImageError">
            <div v-else class="col-1 ml-0 mt-0 relative rounded-[20px] row-1 size-[40px] bg-[#23354f] flex items-center justify-center ring-2 ring-[#162538]">
              <img src="@/assets/sidebar_icons/user.svg" class="size-[20px] opacity-100" />
            </div>
            <!-- Online Indicator -->
            <div class="col-1 ml-[26px] mt-[26px] relative row-1 size-[12px] bg-[#34d399] border-[2px] border-[#162538] rounded-full z-10 block" style="position: absolute; bottom: 0; right: 0;"></div>
          </div>
          
          <div class="flex flex-col gap-[2px] items-start text-white overflow-hidden transition-premium nav-text" :class="isCollapsed ? 'collapsed' : 'expanded'">
            <p class="font-['Roboto',sans-serif] font-medium text-[14px] leading-[18px] overflow-hidden w-full m-0 line-clamp-2 title-tooltip" :title="formatDoctorName()">{{ formatDoctorName() }}</p>
            <p class="font-['Roboto',sans-serif] font-light text-[11px] text-white/70 leading-[14px] overflow-hidden w-full m-0 line-clamp-2" :title="formatUserMedicalInfo()">{{ formatUserMedicalInfo() }}</p>
            <p v-if="getLocationSummaryText()" class="font-['Roboto',sans-serif] font-light text-[11px] text-white/50 leading-[12px] overflow-hidden w-full m-0 mt-[2px] line-clamp-2" :title="getLocationSummaryText()">{{ getLocationSummaryText() }}</p>
          </div>
        </div>
      </div>
      <div v-else class="flex items-center space-x-3 mb-4 justify-center w-full">
        <HeartbeatLoader />
      </div>
      
      <!-- Logout button -->
      <button 
        @click="logout"
        class="sidebar-item group flex items-center p-[10px] rounded-[10px] text-white/90 hover:text-white hover:bg-red-500/20 transition-premium font-['Roboto',sans-serif] font-light text-[15px] w-full justify-start gap-[16px] nav-link"
        :title="isCollapsed ? $t('common.logout') : ''"
      >
        <div class="svg-icon-wrapper transition-premium">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M2.08331 10.4163H12.9166M12.9166 10.4163L8.74998 14.583M12.9166 10.4163L8.74998 6.24967M17.5 16.6663V3.33301" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </div>
        <span class="leading-[20px] text-left transition-premium overflow-hidden nav-text" :class="isCollapsed ? 'collapsed' : 'expanded'">{{ $t('common.logout') }}</span>
      </button>
    </div>
  </div>
</template>

<script>
import { useRouter } from 'vue-router'
import api from '../services/api'
import organizationsApi from '../services/organizationsApi'
import sessionBootstrap, { resetSessionBootstrap } from '../state/sessionBootstrap'
import LanguageSwitcher from './LanguageSwitcher.vue'
import HeartbeatLoader from './common/HeartbeatLoader.vue'
import {
  LEFT_SIDEBAR_KEY,
  RIGHT_SIDEBAR_KEY,
  SIDEBAR_SYNC_EVENT,
  setSidebarCollapsedState
} from '../utils/sidebarSync'
import {
  formatDoctorMedicalInfo,
  getLocalizedCityName as getDisplayCityName,
  getLocalizedClinicName as getDisplayClinicName
} from '../utils/doctorDisplay'
import { canAccessUltrasound } from '../utils/ultrasoundAccess'
import managerQueues from '../state/managerQueues'

const COLLAPSED_SIDEBAR_WIDTH = '72px'
const EXPANDED_SIDEBAR_WIDTH = '260px'

export default {
  name: 'DoctorSidebar',
  components: {
    LanguageSwitcher,
    HeartbeatLoader
  },
  data() {
    // Восстанавливаем состояние сайдбара из localStorage или по умолчанию открыто
    const savedCollapsed = localStorage.getItem(LEFT_SIDEBAR_KEY)
    return {
      userInfo: null,
      userType: null,
      staffRole: null,
      menuItems: [],
      doctorOrganizations: [],
      isCollapsed: savedCollapsed === 'true'
    }
  },
  setup() {
    const router = useRouter()
    return { router }
  },
  async created() {
    await this.checkUserTypeAndFetchInfo()
    this.setMenuItems()
  },
  computed: {
    sessionProfile() {
      return sessionBootstrap.profile
    },
    managerQueues() {
      return managerQueues
    },
    sessionUserType() {
      return sessionBootstrap.userType
    },
    sessionStaffRole() {
      return sessionBootstrap.staffRole
    },
    doctorInfo() {
      // Для обратной совместимости с шаблоном
      return this.userInfo
    },
    // Врач принадлежит к организациям
    hasOrganizations() {
      return Boolean(
        (this.doctorOrganizations && this.doctorOrganizations.length > 0) ||
        this.userInfo?.has_organization_memberships
      )
    },
    hasDirectOrganizations() {
      if (this.userInfo?.has_organization_memberships) {
        return true
      }

      return (this.doctorOrganizations || []).some(
        (organization) => !organization?.access_type || organization.access_type === 'direct'
      )
    },
    hasCorrectionalOrganizations() {
      return (this.doctorOrganizations || []).some(
        organization => organization?.organization_type === 'correctional'
      )
    }
  },
  watch: {
    sessionProfile: {
      handler() {
        this.checkUserTypeAndFetchInfo()
      },
      deep: true
    },
    '$i18n.locale'() {
      // Обновляем пункты меню при смене языка
      this.setMenuItems()
    },
    doctorOrganizations() {
      // Перестраиваем меню когда загружаются организации
      this.setMenuItems()
    }
  },
  methods: {
    async checkUserTypeAndFetchInfo() {
      try {
        this.userType = sessionBootstrap.userType
        this.staffRole = sessionBootstrap.staffRole
        this.userInfo = sessionBootstrap.profile

        if (this.userType === 'doctor') {
          await this.loadDoctorOrganizations()
        }
      } catch (error) {
        // Error handled silently
      }
    },
    async loadDoctorOrganizations() {
      try {
        const response = await organizationsApi.getDoctorOrganizations()
        this.doctorOrganizations = response.results || response || []
      } catch (error) {
        console.error('Error loading doctor organizations:', error)
        this.doctorOrganizations = []
      }
    },
    setMenuItems() {
      if (this.userType === 'staff' && this.staffRole === 'clinic_manager') {
        this.menuItems = [
          { name: 'section-work', section: true, label: 'Работа' },
          { name: 'clinic-today', path: '/clinic-today', label: 'Сегодня', iconType: 'svg', iconName: 'SvgIconCalendar', badgeKey: 'actionCount' },
          { name: 'clinic-appointments', path: '/clinic-appointments', label: 'Записи', iconType: 'svg', iconName: 'SvgIconList' },
          { name: 'clinic-checkups', path: '/clinic-checkups', label: 'Check-UP', iconType: 'svg', iconName: 'SvgIconList' },
          { name: 'clinic-patients', path: '/clinic-patients', label: 'Пациенты', iconType: 'svg', iconName: 'SvgIconUsers' },
          { name: 'clinic-schedule', path: '/clinic-schedule', label: 'Расписание врачей', iconType: 'svg', iconName: 'SvgIconClock' },
          { name: 'section-24-7', section: true, label: 'Врачи 24/7' },
          { name: 'clinic-today-24-7', path: '/clinic-today-24-7', label: 'Сегодня 24/7', iconType: 'svg', iconName: 'SvgIconCalendar' },
          { name: 'clinic-appointments-24-7', path: '/clinic-appointments-24-7', label: 'Записи 24/7', iconType: 'svg', iconName: 'SvgIconList' },
          { name: 'section-money', section: true, label: 'Деньги' },
          { name: 'clinic-cash', path: '/clinic-cash', label: 'Касса', iconType: 'svg', iconName: 'SvgIconWallet' },
          { name: 'clinic-finance', path: '/clinic-finance', label: 'Отчеты', iconType: 'svg', iconName: 'SvgIconChart' },
          { name: 'section-clinic', section: true, label: 'Клиника' },
          { name: 'clinic-doctors', path: '/clinic-doctors', label: 'Врачи и кабинеты', iconType: 'svg', iconName: 'SvgIconAccount' },
          { name: 'clinic-settings', path: '/clinic-settings', label: 'Настройки клиники', iconType: 'svg', iconName: 'SvgIconSliders' }
        ]
        return
      }

      if (this.userType === 'doctor') {
        const hasClinic = !!(this.userInfo && this.userInfo.clinic)
        const items = [
          { name: 'home', path: '/', label: this.$t('navigation.home'), iconType: 'svg', iconName: 'SvgIconChart' },
          { name: 'calendar', path: '/doctor-schedule', label: this.$t('navigation.schedule'), iconType: 'svg', iconName: 'SvgIconList' }
        ]

        // Пациенты клиники доступны всем её врачам; УЗИ — только пилотным участникам.
        if (hasClinic) {
          items.push({ name: 'patients', path: '/patients', label: this.$t('navigation.patients'), iconType: 'svg', iconName: 'SvgIconUsers' })
        }
        if (this.userInfo?.clinic_is_commercial && this.userInfo?.has_checkups) items.push({ name: 'doctor-checkups', path: '/doctor-checkups', label: 'Check-UP', iconType: 'svg', iconName: 'SvgIconList' })
        if (canAccessUltrasound(this.userInfo)) {
          items.push({ name: 'ultrasound', path: '/ultrasound', label: this.$t('navigation.ultrasound'), iconType: 'svg', iconName: 'SvgIconUltrasound' })
        }

        // Пациенты организации и лица ELSYS
        if (this.hasOrganizations) {
          items.push({
            name: 'organization-patients',
            path: '/organization-patients',
            label: this.$t('navigation.organizationPatients') || 'Пациенты организации',
            iconType: 'svg', iconName: 'SvgIconUsers'
          })
        }
        if (this.hasCorrectionalOrganizations) {
          items.push({
            name: 'correctional-institutions',
            path: '/correctional-institutions',
            label: 'Специальные учреждения',
            iconType: 'svg', iconName: 'SvgIconShield'
          })
        }
        if (this.hasDirectOrganizations) {
          items.push({
            name: 'detected-faces',
            path: '/detected-faces',
            label: this.$t('navigation.detectedFaces') || 'Зафиксированные лица',
            iconType: 'svg', iconName: 'SvgIconFlame'
          })
          items.push({
            name: 'survey-results',
            path: '/psychological-monitoring',
            label: 'Результаты опроса',
            iconType: 'svg', iconName: 'SvgIconPulse'
          })
        }

        items.push({ name: 'appointments', path: '/appointments', label: this.$t('navigation.appointments'), iconType: 'svg', iconName: 'SvgIconFlame' })
        items.push({ name: 'support', path: '/support', label: this.$t('navigation.support'), iconType: 'svg', iconName: 'SvgIconPulse' })
        items.push({ name: 'profile', path: '/profile', label: this.$t('navigation.profile'), iconType: 'svg', iconName: 'SvgIconAccount' })
        items.push({ name: 'certificate', path: '/certificate', label: this.$t('navigation.certificate'), iconType: 'svg', iconName: 'SvgIconBook' })

        this.menuItems = items
        return
      }

      // Базовые пункты для медсестры
      const baseItems = [
        { name: 'home', path: '/', label: this.$t('navigation.home'), iconType: 'svg', iconName: 'SvgIconChart' },
        { name: 'patients', path: '/patients', label: this.$t('navigation.patients'), iconType: 'svg', iconName: 'SvgIconUsers' },
        { name: 'profile', path: '/profile', label: this.$t('navigation.profile'), iconType: 'svg', iconName: 'SvgIconAccount' },
        { name: 'certificate', path: '/certificate', label: this.$t('navigation.certificate'), iconType: 'svg', iconName: 'SvgIconShield' }
      ]

      if (this.userType === 'nurse') {
        baseItems.splice(2, 0, { name: 'calls-history', path: '/nurse-calls-history', label: this.$t('navigation.callsHistory'), iconType: 'svg', iconName: 'SvgIconList' })
      }

      this.menuItems = baseItems
    },
    formatDoctorName() {
      if (!this.userInfo || !this.userInfo.user) return ''
      
      const user = this.userInfo.user
      const parts = []
      
      if (user.last_name) parts.push(user.last_name)
      if (user.first_name) parts.push(user.first_name)
      if (user.middle_name) parts.push(user.middle_name)

      const hasEncryptedParts = parts.some(
        (part) => typeof part === 'string' && part.startsWith('ENC::')
      )
      if (hasEncryptedParts) {
        return this.userType === 'staff' && this.staffRole === 'clinic_manager'
          ? 'Менеджер клиники'
          : ''
      }
      
      if (this.userType === 'staff' && this.staffRole === 'clinic_manager') {
        return parts.join(' ') || 'Менеджер клиники'
      }

      return parts.join(' ') || (this.userType === 'nurse' ? this.$t('navigation.medical') : this.$t('doctor.doctors'))
    },
    formatUserMedicalInfo() {
      if (!this.userInfo) {
        return this.userType === 'nurse' ? this.$t('navigation.medical') : this.$t('doctor.doctors')
      }

      if (this.userType === 'staff' && this.staffRole === 'clinic_manager') {
        return 'Менеджер клиники'
      }

      if (this.userType === 'doctor') {
        return formatDoctorMedicalInfo(this.userInfo) || this.$t('doctor.doctors')
      }

      return this.userInfo.specialization || this.$t('navigation.medical')
    },
    getLocationCityLabel() {
      if (this.userType === 'doctor' && this.hasOrganizations) {
        return this.$t('organizationPatients.organization') || 'Организация'
      }

      return this.userType === 'doctor'
        ? this.$t('doctor.doctorCityLabel')
        : this.$t('location.city')
    },
    getDoctorOrganizationNames() {
      if (this.userType !== 'doctor' || !this.hasOrganizations) return ''

      const organizationMemberships = this.doctorOrganizations.length > 0
        ? this.doctorOrganizations
        : (this.userInfo?.organization_memberships || [])

      return [...organizationMemberships]
        .sort((a, b) => Number(Boolean(b?.is_primary)) - Number(Boolean(a?.is_primary)))
        .map(membership => membership?.organization_name || membership?.name)
        .filter(Boolean)
        .join(', ')
    },
    getLocationCityName() {
      if (this.userType === 'doctor' && this.hasOrganizations) {
        return this.getDoctorOrganizationNames()
      }

      return getDisplayCityName(this.userInfo?.city, this.$i18n?.locale)
    },
    getLocationSummaryText() {
      const locationName = this.getLocationCityName()
      if (!locationName) return ''

      if (this.userType === 'doctor' && this.hasOrganizations) {
        return locationName
      }

      return `${this.getLocationCityLabel()}: ${locationName}`
    },
    getLocationClinicName() {
      return getDisplayClinicName(this.userInfo?.clinic, this.$i18n?.locale)
    },
    formatLocation() {
      if (!this.userInfo) return ''
      const parts = []
      const cityName = this.getLocationCityName()
      const clinicName = this.getLocationClinicName()
      if (cityName) parts.push(cityName)
      if (clinicName) parts.push(clinicName)

      return parts.join(', ')
    },
    getLocalizedClinicName(clinic) {
      return getDisplayClinicName(clinic, this.$i18n?.locale)
    },
    isActive(path) {
      // Вложенные страницы менеджера (карточка пациента) подсвечивают свой раздел.
      return this.$route.path === path || (path.startsWith('/clinic-') && this.$route.path.startsWith(`${path}/`))
    },
    async logout() {
      try {
        await api.logout()
        resetSessionBootstrap()
        this.router.push('/login')
      } catch (error) {
        // Even if logout fails, redirect to login
        resetSessionBootstrap()
        this.router.push('/login')
      }
    },
    onImageError(event) {
      // Скрываем изображение если оно не загрузилось
      event.target.style.display = 'none'
    },
    handleSidebarSync(event) {
      const { key, collapsed } = event.detail || {}
      if (key === LEFT_SIDEBAR_KEY && typeof collapsed === 'boolean') {
        this.isCollapsed = collapsed
        this.syncSidebarWidth(collapsed)
      }
    },
    syncSidebarWidth(collapsed) {
      document.documentElement.style.setProperty(
        '--sidebar-width',
        collapsed ? COLLAPSED_SIDEBAR_WIDTH : EXPANDED_SIDEBAR_WIDTH
      )
    },
    toggleSidebar() {
      const nextCollapsed = !this.isCollapsed
      this.isCollapsed = nextCollapsed
      this.syncSidebarWidth(nextCollapsed)
      setSidebarCollapsedState(LEFT_SIDEBAR_KEY, nextCollapsed)

      if (!nextCollapsed) {
        setSidebarCollapsedState(RIGHT_SIDEBAR_KEY, true)
      }
    }
  },
  mounted() {
    this.syncSidebarWidth(this.isCollapsed)
    window.addEventListener(SIDEBAR_SYNC_EVENT, this.handleSidebarSync)
  },
  beforeUnmount() {
    window.removeEventListener(SIDEBAR_SYNC_EVENT, this.handleSidebarSync)
  }
}
</script>

<style scoped>
/* Sidebar motion follows common drawer/navigation patterns:
   short durations, standard easing, no blur/overshoot, and
   reduced-motion support. */
.sidebar-shell {
  --sidebar-ease: cubic-bezier(0.4, 0, 0.2, 1);
  --sidebar-duration: 220ms;
  --sidebar-duration-fast: 160ms;
  --sidebar-width-text: 200px;
  background: #162538;
  border-right: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.14);
  overflow: visible;
  transition:
    width var(--sidebar-duration) var(--sidebar-ease),
    box-shadow var(--sidebar-duration-fast) ease,
    border-color var(--sidebar-duration-fast) ease;
}

.sidebar-shell.is-collapsed {
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.1);
}

.transition-premium {
  transition-property: opacity, transform, color, background-color, border-color, box-shadow;
  transition-duration: var(--sidebar-duration-fast);
  transition-timing-function: var(--sidebar-ease);
}

.sidebar-header {
  transition:
    padding var(--sidebar-duration) var(--sidebar-ease),
    border-color var(--sidebar-duration-fast) ease;
}

.sidebar-brand {
  min-width: 32px;
  transform-origin: left center;
  transition:
    opacity var(--sidebar-duration-fast) ease,
    transform var(--sidebar-duration) var(--sidebar-ease);
}

.sidebar-language {
  transition:
    opacity 120ms ease,
    transform 180ms var(--sidebar-ease);
}

.sidebar-nav {
  transition: padding var(--sidebar-duration) var(--sidebar-ease);
}

.sidebar-shell.is-collapsed .sidebar-nav {
  padding-left: 12px;
  padding-right: 12px;
}

/* Navigation Links Hover Effect */
.nav-link {
  transition:
    background-color var(--sidebar-duration-fast) ease,
    color var(--sidebar-duration-fast) ease;
}

.sidebar-item {
  min-height: 44px;
  transition:
    gap var(--sidebar-duration) var(--sidebar-ease),
    padding var(--sidebar-duration) var(--sidebar-ease),
    background-color var(--sidebar-duration-fast) ease,
    color var(--sidebar-duration-fast) ease;
}

.sidebar-shell.is-expanded .sidebar-item {
  justify-content: flex-start;
  gap: 16px;
  padding-left: 10px;
  padding-right: 10px;
}

.sidebar-shell.is-collapsed .sidebar-item {
  justify-content: center;
  gap: 0;
  padding-left: 0;
  padding-right: 0;
}

.nav-text {
  flex: 1 1 auto;
  min-width: 0;
  transform-origin: left center;
  transition:
    opacity 120ms ease,
    transform 180ms var(--sidebar-ease),
    max-width var(--sidebar-duration) var(--sidebar-ease);
}

.nav-text.expanded {
  max-width: var(--sidebar-width-text);
  opacity: 1;
  transform: translateX(0);
  white-space: normal;
}

.nav-text.collapsed {
  flex-basis: 0;
  max-width: 0;
  opacity: 0;
  transform: translateX(-8px);
  pointer-events: none;
  white-space: nowrap;
}

.sidebar-profile-card {
  transition:
    gap var(--sidebar-duration) var(--sidebar-ease),
    padding var(--sidebar-duration) var(--sidebar-ease),
    background-color var(--sidebar-duration-fast) ease;
}

.sidebar-shell.is-expanded .sidebar-profile-card {
  gap: 12px;
  padding-left: 12px;
  padding-right: 12px;
}

.sidebar-shell.is-collapsed .sidebar-profile-card {
  justify-content: center;
  gap: 0;
  padding-left: 0;
  padding-right: 0;
}

.brand-switch-enter-active,
.brand-switch-leave-active {
  transition:
    opacity 120ms ease,
    transform 180ms var(--sidebar-ease);
}

.brand-switch-enter-from {
  opacity: 0;
  transform: translateX(-6px);
}

.brand-switch-leave-to {
  opacity: 0;
  transform: translateX(6px);
}

/* Custom Scrollbar */
nav::-webkit-scrollbar {
  width: 4px;
}

nav::-webkit-scrollbar-track {
  background: transparent;
}

nav::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
}

nav::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.2);
}

/* Toggle button (Premium) */
.toggle-btn {
  position: absolute;
  top: 66px;
  right: -14px;
  width: 28px;
  height: 28px;
  background: #162538;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 60;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.18);
  transition:
    transform var(--sidebar-duration-fast) ease,
    background-color var(--sidebar-duration-fast) ease,
    border-color var(--sidebar-duration-fast) ease,
    box-shadow var(--sidebar-duration-fast) ease;
}

.toggle-btn:hover {
  background: #1d3046;
  border-color: rgba(255, 255, 255, 0.32);
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.22);
}

.toggle-btn:active {
  transform: scale(0.96);
}

.toggle-btn:focus-visible {
  outline: 2px solid rgba(255, 255, 255, 0.55);
  outline-offset: 3px;
}

.toggle-icon {
  transition: transform 180ms var(--sidebar-ease);
  color: white;
}

.toggle-icon.rotated {
  transform: rotate(180deg);
}

.svg-icon-wrapper {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: color var(--sidebar-duration-fast) ease;
}

.logo-icon-wrapper {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

@media (prefers-reduced-motion: reduce) {
  .sidebar-shell,
  .transition-premium,
  .sidebar-item,
  .nav-text,
  .toggle-btn,
  .toggle-icon,
  .brand-switch-enter-active,
  .brand-switch-leave-active {
    transition-duration: 1ms !important;
    animation: none !important;
  }
}
</style>
